<script setup>
import { HrChip } from '../primitives';

/**
 * Suggestion chip — a static chip for displaying prompts, recommendations, or keyword tags.
 *
 * @status recommended
 * @group chips
 * @useWhen Presenting clickable suggestions or topic tags alongside a search or input field
 * @avoidWhen A toggle state is needed — use HrFilterChip
 * @related HrAssistChip
 * @event click Fired when the chip is activated.
 */
defineOptions({ name: 'HrSuggestionChip' });

defineProps({
  /**
   * Chip label text. Falls back to the default slot when null.
   * @replacesSlot default
   * @example Try: Add a photo
   */
  label: {
    type: String,
    default: null,
  },
  /**
   * Chip size.
   * @values sm, md, lg
   */
  size: {
    type: String,
    default: 'md',
    validator: (v) => ['sm', 'md', 'lg'].includes(v),
  },
  /** Prevents interaction and dims the chip. */
  disable: {
    type: Boolean,
    default: false,
  },
  /** Renders a skeleton placeholder instead of the chip. */
  pending: {
    type: Boolean,
    default: false,
  },
});

// @todo Remove legacy-props when Legacy theme is removed
const legacyProps = { outline: true };
</script>

<template>
  <hr-chip
    variant="suggestion"
    clickable
    :label="label"
    :size="size"
    :disable="disable"
    :pending="pending"
    :legacy-props="legacyProps"
    v-bind="$attrs"
  >
    <!-- @slot default Custom chip label content; use instead of the `label` prop for rich content. -->
    <slot />
  </hr-chip>
</template>
